import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { ArrowRight } from "lucide-react";
import { DISCORD_URL } from "../../lib/site";
import { useI18n } from "../../i18n/LanguageContext";
import { DiscordIcon } from "./DiscordIcon";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fade = (delay) => ({
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] } },
});

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.12em]">
    <motion.span variants={line} custom={i} className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t } = useI18n();
  const lenis = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  const goProducts = () =>
    lenis ? lenis.scrollTo("#products", { offset: -80 }) : document.querySelector("#products")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section ref={ref} className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 -z-10">
        <img
          src="https://images.unsplash.com/photo-1672872476232-da16b45c9001?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
          alt=""
          className="h-full w-full object-cover opacity-[0.22]"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#080a0f] via-[#080a0f]/70 to-[#080a0f]" />
      </motion.div>
      <div className="absolute inset-0 -z-10 grid-lines opacity-40" />
      <div className="absolute inset-0 -z-10 noise opacity-[0.04] pointer-events-none" />
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 h-[420px] w-[620px] rounded-full bg-cyan-500/10 blur-[120px] -z-10" />

      <motion.div style={{ y: contentY, opacity: contentOpacity }} className="max-w-5xl mx-auto px-5 md:px-8 pt-28 pb-16 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2.5 rounded-full border border-white/10 glass px-4 py-1.5 mb-9"
          data-testid="hero-badge"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-cyan-400" />
          </span>
          <span className="text-sm text-slate-300">{t.hero.badge}</span>
        </motion.div>

        <motion.h1
          key={t.hero.title1}
          initial="hidden"
          animate="show"
          className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-6xl lg:text-7xl"
          data-testid="hero-title"
        >
          <MaskLine i={0}>{t.hero.title1}</MaskLine>
          <MaskLine i={1}>
            <span className="text-gradient">{t.hero.title2}</span>
          </MaskLine>
        </motion.h1>

        <motion.p
          variants={fade(0.55)}
          initial="hidden"
          animate="show"
          className="mt-8 mx-auto max-w-xl text-base md:text-lg text-slate-400 leading-relaxed"
        >
          {t.hero.subtitle}
        </motion.p>

        <motion.div
          variants={fade(0.7)}
          initial="hidden"
          animate="show"
          className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3"
        >
          <button
            data-testid="hero-view-products"
            onClick={goProducts}
            className="group bg-brand text-[#080a0f] font-semibold px-6 py-3.5 rounded-full flex items-center gap-2 hover:shadow-[0_0_30px_rgba(56,189,248,0.45)] transition-shadow duration-300 active:scale-[0.98]"
          >
            {t.hero.viewProducts}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            data-testid="hero-contact-discord"
            href={DISCORD_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="border border-white/12 bg-white/[0.03] text-white font-medium px-6 py-3.5 rounded-full flex items-center gap-2 hover:bg-white/[0.07] hover:border-white/20 transition-colors duration-300 active:scale-[0.98]"
          >
            <DiscordIcon className="h-4 w-4 text-cyan-400" />
            {t.hero.contactDiscord}
          </a>
        </motion.div>

        <motion.div
          variants={fade(0.9)}
          initial="hidden"
          animate="show"
          className="mt-20 pt-10 border-t border-white/5 grid grid-cols-3 gap-4 max-w-2xl mx-auto"
        >
          {t.stats.map((s) => (
            <div key={s.label} data-testid={`stat-${s.value}`} className="text-center">
              <div className="font-display font-bold text-3xl md:text-4xl text-gradient">{s.value}</div>
              <div className="mt-2 text-xs md:text-sm text-slate-500">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
